import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  CBadge, CButton, CCol, CFormInput, CFormLabel, CRow,
  CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CSpinner,
} from '@coreui/react'
import { api } from '../api/client'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'

const colorEstado = (estado: string) => {
  if (estado === 'Activo') return 'success'
  if (estado === 'Bloqueado' || estado === 'Inactivo') return 'danger'
  return 'warning'
}

// Usuarios GLOBALES (todas las empresas) — ruta /superadmin/usuarios.
// El breadcrumb lo muestra el AppHeader global; el título va debajo.
const SuperAdminUsuariosPage: React.FC = () => {
  const [usuarios, setUsuarios] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [exito, setExito] = useState('')
  const [claveUsuario, setClaveUsuario] = useState<any | null>(null)
  const [nuevaClave, setNuevaClave] = useState('')
  const [confirmacion, setConfirmacion] = useState('')
  const [modalError, setModalError] = useState('')
  const [guardandoClave, setGuardandoClave] = useState(false)

  // Listado global de usuarios — réplica del listado de usuarioww.cs
  // (webobject usuario, la misma proyección que UsuariosController GET:
  // UsuarioCodigo, Email, Nombre, Estado, Rol). El superadmin ve TODAS las
  // empresas (su token no lleva claim de empresa — EmpresaResolver usa query).
  const cargar = useCallback(() => {
    setCargando(true)
    api.get<any[]>('/superadmin/usuarios')
      .then(setUsuarios)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }, [])

  useEffect(() => { cargar() }, [cargar])

  // Bloquear/desbloquear usuario — réplica del DO UPDATE de usuarioww.cs
  // (usuario Mode="UPD", usuarioww.cs:2588-2600 → usuario_bc.cs:1690): el
  // UsuarioEstado distinto de "Activo" bloquea el login (AuthService.cs:37).
  const toggleEstado = async (row: any) => {
    setError('')
    setExito('')
    const nuevo = row.usuarioEstado === 'Activo' ? 'Bloqueado' : 'Activo'
    try {
      await api.put(`/superadmin/usuarios/${row.usuarioCodigo}/estado`, { estado: nuevo })
      setExito(`Usuario ${row.usuarioCodigo} ${nuevo === 'Activo' ? 'desbloqueado' : 'bloqueado'} correctamente`)
      cargar()
    } catch (e: any) {
      setError(e.message)
    }
  }

  const abrirCambiarClave = (row: any) => {
    setModalError('')
    setNuevaClave('')
    setConfirmacion('')
    setClaveUsuario(row)
  }

  // Cambiar contraseña de otro usuario — réplica de gamchangepassword.cs
  // (GAM setpassword): valida mínimo 6 y confirmación; persiste UsuarioClave
  // (clave plana, convención GX/LowCode). El superadmin lo hace globalmente.
  const guardarClave = async (e: React.FormEvent) => {
    e.preventDefault()
    setModalError('')
    if (nuevaClave.length < 6) {
      setModalError('La nueva clave debe tener al menos 6 caracteres')
      return
    }
    if (nuevaClave.length > 15) {
      setModalError('La nueva clave no puede tener más de 15 caracteres')
      return
    }
    if (nuevaClave !== confirmacion) {
      setModalError('La confirmación no coincide con la nueva clave')
      return
    }
    setGuardandoClave(true)
    try {
      await api.put(`/superadmin/usuarios/${claveUsuario.usuarioCodigo}/clave`, { nuevaClave, confirmacion })
      setExito(`Clave del usuario ${claveUsuario.usuarioCodigo} cambiada correctamente`)
      setClaveUsuario(null)
    } catch (err: any) {
      setModalError(err.message)
    } finally {
      setGuardandoClave(false)
    }
  }

  const columnas: WwColumn[] = [
    { key: 'usuarioCodigo', title: 'Usuario' },
    { key: 'usuarioNombre', title: 'Nombre' },
    { key: 'usuarioEmail', title: 'Email' },
    { key: 'empresaCodigo', title: 'Empresa' },
    { key: 'rolNombre', title: 'Rol' },
    {
      key: 'usuarioEstado', title: 'Estado',
      render: (row) => <CBadge color={colorEstado(row.usuarioEstado ?? '')}>{row.usuarioEstado || '—'}</CBadge>,
      // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
      // exportValue espeja el render (estado o fallback '—').
      exportValue: (row) => row.usuarioEstado || '—',
    },
    { key: 'usuarioOnline', title: 'Online', type: 'bool' },
    {
      key: 'acciones', title: 'Acciones', minWidth: 230,
      render: (row) => (
        <div className="d-flex gap-1">
          <CButton
            size="sm"
            color={row.usuarioEstado === 'Activo' ? 'danger' : 'success'}
            disabled={row.usuarioCodigo === 'admin'}
            onClick={() => toggleEstado(row)}
          >
            {row.usuarioEstado === 'Activo' ? 'Bloquear' : 'Desbloquear'}
          </CButton>
          <CButton size="sm" color="outline-secondary" onClick={() => abrirCambiarClave(row)}>
            Cambiar Clave
          </CButton>
        </div>
      ),
    },
  ]

  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4 className="mb-0">Usuarios</h4>
          <small className="text-body-secondary">
            Todas las empresas — bloquear/desbloquear acceso y cambiar contraseña.
          </small>
        </CCol>
      </CRow>

      {error && <div className="alert alert-danger">{error}</div>}
      {exito && <div className="alert alert-success">{exito}</div>}

      {cargando ? (
        <div className="text-center py-4"><CSpinner color="primary" /></div>
      ) : (
        <WwGrid title="Usuarios" columns={columnas} rows={usuarios} onTotalChange={() => undefined} />
      )}

      <CModal visible={claveUsuario !== null} onClose={() => setClaveUsuario(null)}>
        <CModalHeader>
          <CModalTitle>Cambiar clave — {claveUsuario?.usuarioCodigo ?? ''}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {modalError && <div className="alert alert-danger">{modalError}</div>}
          <form ref={formRef} onSubmit={guardarClave}>
            <CFormLabel>Nueva clave (mínimo 6 caracteres)</CFormLabel>
            <CFormInput
              type="password"
              className="mb-3"
              value={nuevaClave}
              maxLength={15}
              onChange={(e) => setNuevaClave(e.target.value)}
            />
            <CFormLabel>Confirmación</CFormLabel>
            <CFormInput
              type="password"
              value={confirmacion}
              maxLength={15}
              onChange={(e) => setConfirmacion(e.target.value)}
            />
          </form>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" disabled={guardandoClave} onClick={() => setClaveUsuario(null)}>
            Cancelar
          </CButton>
          <CButton color="primary" type="button" disabled={guardandoClave} onClick={() => formRef.current?.requestSubmit()}>
            {guardandoClave ? <CSpinner size="sm" /> : 'Guardar'}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default SuperAdminUsuariosPage
